import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { UserSearchControl } from '../user-search-control/user-search-control';
import { Dtos } from '#models';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { formatFullName } from 'src/core/pipes/full-name-pipe';
import { Button } from 'primeng/button';
import { StoreService } from '#services';
import { UserService } from '#services';
import { RadioButton } from 'primeng/radiobutton';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { TranslatePipe } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-shared-user-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    UserSearchControl,
    TranslatePipe,
    InputText,
    Button,
    InputGroup,
    InputGroupAddon,
    RadioButton,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SharedUserForm),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SharedUserForm),
      multi: true,
    },
  ],
  templateUrl: './shared-user-form.html',
  styleUrl: './shared-user-form.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedUserForm implements ControlValueAccessor, Validator {
  isReadOnly = input(false);

  private _storeService = inject(StoreService);
  private id = 0;

  canImportSharedUsers = computed(() => {
    const settings = this._storeService.currentBu();

    if (!settings) {
      return false;
    }
    return settings?.CanImportSharedUsers;
  });

  private _initialSharedUsers = signal<Dtos.SharedUser[]>([]);

  formGroup = new FormGroup({
    name: new FormControl<string>('', {
      validators: [Validators.required],
    }),
    email: new FormControl<string>('', {
      validators: [Validators.required, Validators.email],
    }),
    children: new FormArray([]),
    canImport: new FormControl<boolean | null>(null),
  });

  private _destroyRef = inject(DestroyRef);

  writeValue(obj: Dtos.User): void {
    this.id = obj?.ID;

    if (obj !== null) {
      this._setChildren(obj.ChildUsers || []);

      this._initialSharedUsers.set(obj.ChildUsers || []);
    }

    this.formGroup.patchValue(
      {
        email: obj ? obj.Email : '',
        name: obj ? obj.FirstName : '',
      },
      { emitEvent: false }
    );
    this.formGroup.patchValue({
      canImport: obj ? obj.IsTcoWhitePagesImported || false : null,
    });
    this.formGroup.updateValueAndValidity({ emitEvent: false });
  }

  private _setChildren(obj: Dtos.SharedUser[], emitEvent = false) {
    this.childrenGroup.clear({ emitEvent: false });

    obj.forEach(child => {
      this.childrenGroup.push(new FormControl<Dtos.SharedUser>(child as Dtos.SharedUser), { emitEvent });
    });
  }

  registerOnChange(fn: (value: Dtos.User) => void): void {
    this.formGroup.valueChanges
      .pipe(
        filter(data => !!data),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(value => {
        fn({
          ID: this.id,
          FirstName: value.name ?? '',
          Email: value.email ?? '',
          ChildUsers: value.children ?? ([] as Dtos.SharedUser[]),
          IsTcoWhitePagesImported: value.canImport ?? false,
        } as Dtos.User);
      });
  }

  registerOnTouched(fn: () => void): void {
    this.formGroup.statusChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      if (this.formGroup.touched) {
        fn();
      }
    });
  }

  validate(): ValidationErrors | null {
    const control = this.formGroup;
    let errors = {};
    if (control.get('name')?.invalid) {
      errors = { ...control.get('name')?.errors };
    }
    if (control.get('email')?.invalid) {
      errors = { ...errors, ...control.get('email')?.errors };
    }
    this.childrenGroup.controls.forEach(childControl => {
      if (childControl.invalid) {
        errors = { ...errors, ...childControl.errors };
      }
    });
    return Object.keys(errors).length > 0 ? errors : null;
  }

  get childrenGroup() {
    return this.formGroup.get('children') as FormArray;
  }

  addUser() {
    const children = this.formGroup.get('children') as FormArray;
    children.push(
      new FormControl<Dtos.SharedUser | null>(null, {
        validators: [
          control => {
            if (control.value === null) {
              return { required: true };
            }
            return control.value.Email ? null : { required: true };
          },
        ],
      })
    );
  }

  formatUser = (
    val:
      | Pick<Dtos.User, 'Email' | 'ID' | 'FirstName' | 'LastName' | 'MiddleName'>
      | Pick<Dtos.SharedUser, 'Email' | 'UserID' | 'FullName' | 'ParentUserId'>
      | null
      | string
  ): string | Dtos.SharedUser => {
    if ('string' === typeof val || val === null) {
      return val ?? '';
    }
    if ('ID' in val) {
      // Pick<Dtos.User> path
      return {
        UserID: val.ID,
        Email: val.Email,
        FullName: [val.FirstName, val.MiddleName, val.LastName].filter(Boolean).join(' '),
        ParentUserId: this.id,
      };
    }
    // Pick<Dtos.SharedUser> path
    return {
      UserID: val.UserID,
      Email: val.Email,
      FullName: formatFullName(val as Dtos.SharedUser),
      ParentUserId: this.id,
    };
  };

  get invalidName() {
    return (
      !!this.formGroup.get('name')?.invalid &&
      (this.formGroup.get('name')?.touched || this.formGroup.get('name')?.dirty)
    );
  }

  get invalidEmail() {
    return (
      !!this.formGroup.get('email')?.invalid &&
      (this.formGroup.get('email')?.touched || this.formGroup.get('email')?.dirty)
    );
  }

  removeChildUser(childIndex: number) {
    this.childrenGroup.removeAt(childIndex);
  }

  labelUser(childIndex: number): string {
    return 'User ' + (childIndex + 1);
  }

  onSetImporFromWhitePagesSelected() {
    this._setChildren(this._initialSharedUsers(), true);
  }

  importSharedUsers() {
    this.userService
      .importSharedUser(this._storeService.currentBu()?.Id || 0, this.formGroup.value.email || '')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(({ ChildUsers: childUsers = [] }) => {
        this._setChildren(
          childUsers.map(child => ({
            ...child,
            ParentUserId: this.id,
          })),
          true
        );
      });
  }

  private userService = inject(UserService);

  userCanImportSharedUsers = toSignal(
    this.formGroup.valueChanges.pipe(
      map(value => !!value.canImport),
      startWith(!!this.formGroup.get('canImport')?.value),
      distinctUntilChanged()
    ),
    {
      initialValue: null,
    }
  );
}
